/*
    Provides a React context that resembles a Redux store, containing
    the global application state.

    Based on the concept presented in
    https://blog.logrocket.com/use-hooks-and-context-not-react-and-redux/
*/

import {
  createContext,
  useReducer,
  useEffect,
  ReactNode,
  Dispatch,
} from 'react'
import { BackendProject, Project } from '../types/project'
import * as actions from './actions'
import reducer, { getBoundariesArea, projectToState } from './reducer'
import GeoJSON from 'ol/format/GeoJSON'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'

import PropTypes from 'prop-types'
import OlMap from 'ol/Map'
import ApiClient from '../api'
import OlMultiPolygon from 'ol/geom/MultiPolygon'
import TileServerClient from '../tile-server-client'
import { MultiPolygon } from '@turf/helpers'
import { UserSettings as PlantHealthUserSettings } from '../components/analysis/types'
import { UserSettings as ZonalStatisticsUserSettings } from '../components/zonal-statistics/types'
import { toCamelCase } from '../object-utils'
import { FontFamily } from '../components/reports/types'
import { Currency } from '../utils/payment-utils'
import Ts2Client from '../ts2-client'
import { UserNotification } from './use-notifications'
import { Geometry } from 'ol/geom'
import { CropSource } from '../types/common'

export type Units = 'metric' | 'imperial'

type ToolSettings = {
  plantHealth: PlantHealthUserSettings
  zonalStatistics: ZonalStatisticsUserSettings
}

export type UserSettings = {
  tools?: Partial<ToolSettings>
  units?: 'metric' | 'imperial'
}

/* eslint-disable camelcase */
export type SiblingInfo = {
  id: number
  path: string
  created_at: string
  survey_date?: string
  display_date: string
  bands: string
  altitude: number
  has_zonal_statistics: boolean
}
/* eslint-enable camelcase */

export type PrescriptionState = Record<string, unknown>

export type StateProperties = {
  [key: string]: any
  area?: number
  analysis?: {
    index?: string
    [key: string]: any
  }
  prescription?: PrescriptionState
  hasAnnotations?: boolean
}

export type Permissions = Record<string, boolean>

/**
 * The global application state. This is the state that is shared between
 * components. Most of the state is loaded from the backend, but some of it
 * altered while the application is running.
 *
 * Maintenance notes:
 * There's a lot of legacy around exactly why the state is shaped like it is,
 * and we should look into refactoring it. It's not clear that this should
 * be a single object: look into how we have separated the orthomosaic state
 * in the useOrthoStore hook.
 */
export interface StoreState {
  /**
   * The project that is currently being viewed.
   */
  project: Project
  /**
   * The boundaries of the project, as an OpenLayers geometry, it's the same
   * geometry as boundariesGeoJson but in OpenLayers format and map projection.
   * This object is always set:
   * - If the project has a cropped extent, it is set to the cropped extent.
   * - If the project's field's geometry is set, it is set to the field's geometry.
   * - Otherwise, it is set to the project's original extent.
   */
  boundariesGeometry: Geometry
  /**
   * The boundaries of the project, as a GeoJSON object.
   * This object is always set:
   * - If the project has a cropped extent, it is set to the cropped extent.
   * - If the project's field's geometry is set, it is set to the field's geometry.
   * - Otherwise, it is set to the project's original extent.
   */
  boundariesGeoJson: MultiPolygon
  /**
   * This is a weird mix of attributes that may or may not be available and is used
   * to share data between components. It should be refactored and ideally removed,
   * I don't thing the term "properties" is useful.
   */
  properties: StateProperties
  /**
   * The state of the tools that are available for the current project. This is a map from
   * tool id to tool object.
   */
  tools: Record<string, actions.Tool>
  /**
   * The current user's id.
   */
  userId: number
  /**
   * The permissions of the current user, as a map from permission name to boolean.
   */
  permissions: Permissions
  /**
   * Whether the project is editable; for example, if an anonymous user is viewing a shared project,
   * this will be false.
   */
  editable: boolean
  /**
   * The main map object.
   */
  map?: OlMap
  notifications: Array<UserNotification>
  /**
   * The source of the crop layer. This is a vector source containing a single feature,
   * the boundaries of the project.
   */
  cropSource: CropSource
  apiClient: ApiClient
  tileServerClient: TileServerClient
  ts2Client: Ts2Client
  units: Units
  /**
   * Legacy i18n support; these are still used in a few places but should be removed.
   * If we implement i18n again, we should do it at another level.
   */
  labels: Record<string, string>
  /**
   * The token used to authenticate purchases with Stripe.
   */
  stripeToken: string
  /**
   * The user's selected currency (USD, EUR), if any.
   */
  currency?: Currency
  /**
   * Should in app purchases be allowed
   */
  purchasesEnabled: boolean
  /**
   * The projects that belong to the same field as this project
   */
  projectSiblings: SiblingInfo[]
  /**
   * Whether the user's changes should be persisted to the backend.
   */
  persistChanges: boolean
  /**
   * Mapbox token to use for background map layers.
   */
  mapboxToken: string
  /**
   * The user's settings.
   */
  userSettings: UserSettings
  /**
   * The URL of the logo to display in the reports.
   */
  logoUrl: string
  /**
   * The list of fonts to use in the reports.
   */
  printFonts: FontFamily[]
  /**
   * Indicates if this is a showcase project
   */
  showCase: boolean
  /**
   * The list of vegetation indices that are available for the current project.
   */
  indices: string[]
  /**
   * The project's zonal statistics (areas with geometries and possibly their stats)
   *
   * Note that zonal statistics settings are stored in the project's settings
   */
  zonalStatistics: string | null
}

const initialState = {
  project: {},
  properties: {},
  tools: {},
  permissions: {},
  notifications: [],
  map: undefined,
  cropSource: null,
  stripeToken: '',
  purchasesEnabled: false,
  showCase: false,
}

export type Store = {
  state: StoreState
  dispatch: Dispatch<actions.StoreAction>
}

const store = createContext<Store | undefined>(undefined)
const { Provider } = store

interface StateProvider {
  userId: number
  projectId: number
  editable: boolean
  apiClient: ApiClient
  tileServerClient: TileServerClient
  ts2Client: Ts2Client
  children?: ReactNode
  persistChanges: boolean
  // eslint-disable-next-line camelcase
  project: BackendProject
  units: Units
  labels: Record<string, string>
  purchasesEnabled: boolean
  currency?: Currency
  projectSiblings: SiblingInfo[]
  mapboxToken: string
  userSettings: UserSettings
  logoUrl: string
  printFonts: FontFamily[]
  indices: string[]
}

const StateProvider = (props: StateProvider): JSX.Element => {
  const { apiClient, children } = props

  const [state, dispatch] = useReducer(reducer, props, createFirstState)

  useEffect(() => {
    async function loadPermissions() {
      const permissions = await apiClient.getCurrentPermissions()
      dispatch({
        type: actions.PERMISSIONS_SET,
        permissions,
      })
    }

    loadPermissions()
  }, [apiClient])

  return <Provider value={{ state, dispatch }}>{children}</Provider>
}

export function createFirstState(props: StateProvider): StoreState {
  const { project, boundariesGeometry } = projectToState(props.project)
  const cropSource = new VectorSource<Feature<OlMultiPolygon>>({
    features: [new Feature(boundariesGeometry)],
  })

  const properties = {
    ...project?.metadata?.data,
    area: getBoundariesArea(boundariesGeometry),
  }

  return {
    ...initialState,
    ...props,
    project,
    properties,
    zonalStatistics: null,
    cropSource,
    boundariesGeometry,
    boundariesGeoJson: new GeoJSON().writeGeometryObject(boundariesGeometry, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    }) as MultiPolygon,
    userSettings: toCamelCase(props.userSettings),
  }
}

export { store, StateProvider }

StateProvider.propTypes = {
  project: PropTypes.shape({
    original_extent: PropTypes.object.isRequired,
    cropped_extent: PropTypes.object,
  }).isRequired,
  apiClient: PropTypes.shape({
    updateProject: PropTypes.func,
    getCurrentPermissions: PropTypes.func,
  }).isRequired,
  properties: PropTypes.object,
  projectId: PropTypes.number,
  children: PropTypes.node,
  persistChanges: PropTypes.bool,
}
